import { z } from 'zod'
import { STATUS_USUARIO, type StatusUsuario } from '../enums'
import { CHAVES_PERMISSAO, type ChavePermissao } from '../permissoes'
import type { HorarioDeTrabalho } from '../horario-de-trabalho'
import { textoObrigatorio } from './comum'

/**
 * `proprietario` não aparece em nenhum destes schemas de propósito.
 * A flag é imutável pela interface (4.10) e o servidor recusa qualquer
 * tentativa de defini-la.
 */

export const criarUsuarioSchema = z.object({
  nome: textoObrigatorio('nome', 120),
  email: z.string().trim().toLowerCase().email('e-mail inválido').max(160),
  senha: z.string().min(8, 'a senha precisa de ao menos 8 caracteres').max(200),
  ativo: z.boolean().default(true),
  setores: z
    .array(z.object({ setorId: z.string().uuid(), atende: z.boolean().default(true) }))
    .default([]),
  permissoes: z.array(z.enum(CHAVES_PERMISSAO as [string, ...string[]])).default([]),
})
export type CriarUsuarioEntrada = z.infer<typeof criarUsuarioSchema>

/**
 * Dois presets com o mesmo nome? Sem caixa e sem espaço sobrando: "vendedor " é
 * "Vendedor". A api recusa e a tela avisa pela mesma regra.
 */
export function mesmoNome(a: string, b: string): boolean {
  return a.trim().toLocaleLowerCase('pt-BR') === b.trim().toLocaleLowerCase('pt-BR')
}

export const atualizarUsuarioSchema = z.object({
  nome: textoObrigatorio('nome', 120).optional(),
  email: z.string().trim().toLowerCase().email('e-mail inválido').max(160).optional(),
  ativo: z.boolean().optional(),
  status: z.enum(STATUS_USUARIO).optional(),
})
export type AtualizarUsuarioEntrada = z.infer<typeof atualizarUsuarioSchema>

/** Só o proprietário redefine a senha de outra pessoa (decisão 10.6). */
export const definirSenhaSchema = z.object({
  senha: z.string().min(8, 'a senha precisa de ao menos 8 caracteres').max(200),
})

export const definirSetoresSchema = z.object({
  setores: z.array(z.object({ setorId: z.string().uuid(), atende: z.boolean().default(true) })),
})

export const definirPermissoesSchema = z.object({
  permissoes: z.array(z.enum(CHAVES_PERMISSAO as [string, ...string[]])),
})

/**
 * Um conjunto de permissões com nome, para aplicar de uma vez na janela de
 * permissões (pedido e questionário de 06/10/2026). Substitui o botão fixo
 * "Padrão de vendedor", que virou o primeiro preset da lista.
 *
 * Guarda só as permissões: números e setores mudam de pessoa para pessoa.
 */
export interface PresetDePermissaoDto {
  id: string
  nome: string
  permissoes: ChavePermissao[]
}

/**
 * Salvar com um nome que já existe atualiza aquele preset; nome novo cria
 * outro. As chaves chegam como texto e o servidor filtra as que existem:
 * permissão que deixar de existir some do preset, em vez de travar o salvar.
 */
export const salvarPresetSchema = z.object({
  nome: textoObrigatorio('nome', 60),
  permissoes: z.array(z.string().max(80)).max(500),
})

export const renomearPresetSchema = z.object({ nome: textoObrigatorio('nome', 60) })

export const idDePresetSchema = z.object({ id: z.string().min(1).max(60) })

export interface UsuarioSetorDto {
  setorId: string
  nome: string
  atende: boolean
}

export interface UsuarioDto {
  id: string
  nome: string
  email: string
  proprietario: boolean
  status: StatusUsuario
  ativo: boolean
  criadoEm: string
  ultimoAcessoEm: string | null
  setores: UsuarioSetorDto[]
  /** Números de WhatsApp liberados para a pessoa (sem `numeros.todos`, só estes). */
  conexaoIds: string[]
  permissoes: ChavePermissao[]
  /**
   * Os dias e horas em que a pessoa pode usar o CRM (10/10/2026). Nulo é sem
   * horário: quem não é o proprietário não entra até alguém definir.
   */
  horarioDeTrabalho: HorarioDeTrabalho | null
}

/**
 * Um colega, como as telas de trabalho precisam dele: o nome para escolher em
 * Pedir apoio, Transferir, responsável da tarefa e dono do contato, e o status
 * para saber quem está ali (mapa de uso, 08/10/2026).
 *
 * Sem e-mail, setores, números nem permissões: isso é da administração. Antes
 * essas listas vinham da lista de usuários, que só quem abre a Configuração
 * recebe, e para o vendedor elas ficavam vazias: ele não conseguia pedir
 * apoio nem transferir para ninguém.
 */
export type MembroDaEquipeDto = Pick<
  UsuarioDto,
  'id' | 'nome' | 'ativo' | 'status' | 'proprietario'
>

/** Os números de WhatsApp que uma pessoa vê, pela janela de permissões (ADR 0038). */
export const definirConexoesDoUsuarioSchema = z.object({
  conexaoIds: z.array(z.string().uuid()).max(200),
})

/**
 * Desligamento (ADR 0036): desativar alguém passa o que é vivo dele para outra
 * pessoa, ou para a fila. O histórico (vendas, conversas encerradas,
 * estatísticas) fica com quem saiu.
 */
export const desligarUsuarioSchema = z.object({
  /** Quem herda. `null` devolve para a fila, e o rodízio distribui. */
  paraUsuarioId: z.string().uuid().nullable(),
})

/** O que a pessoa tem hoje, para a janela mostrar antes de confirmar. */
export interface ResumoDoDesligamento {
  /** Contatos em que ela é a dona (as negociações abertas vão junto). */
  clientes: number
  negociacoesAbertas: number
  empresas: number
  conversasAbertas: number
  /** Conversas de outros em que ela estava de apoio: o apoio só sai. */
  apoios: number
  tarefas: number
  agendadas: number
}

export interface ResultadoDoDesligamento extends ResumoDoDesligamento {
  /** Nome de quem herdou, ou `null` quando foi para a fila. */
  paraNome: string | null
}

/**
 * "142 clientes, 6 conversas abertas e 3 tarefas": o resumo em uma frase, só
 * com o que existe. Usado no aviso de quem herdou.
 */
export function fraseDoResumo(resumo: ResumoDoDesligamento): string {
  const partes: [number, string, string][] = [
    [resumo.clientes, 'cliente', 'clientes'],
    [resumo.negociacoesAbertas, 'negociação aberta', 'negociações abertas'],
    [resumo.empresas, 'empresa', 'empresas'],
    [resumo.conversasAbertas, 'conversa aberta', 'conversas abertas'],
    [resumo.tarefas, 'tarefa', 'tarefas'],
    [resumo.agendadas, 'mensagem agendada', 'mensagens agendadas'],
  ]
  const itens = partes
    .filter(([n]) => n > 0)
    .map(([n, um, varios]) => `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`)
  if (itens.length === 0) return 'Nada para passar adiante.'
  if (itens.length === 1) return `${itens[0]}.`
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}.`
}
